/**
 * A hover/focus tooltip. Small, because the product needs it for exactly the
 * cases the design names and no more: `ties resolve UP` on the strike meter's
 * tie hairline, `clock re-syncing` on a soft-disabled one-click entry, the
 * disclosed taker delay in the ticket's info popover, and the reason on a
 * disabled button.
 *
 * Every one of those is an *explanation of a rule the user is about to be
 * affected by*, which is why the tooltip is keyboard-reachable and announced:
 * `aria-describedby` rather than `title`, so it is not restricted to a pointer.
 * `<Button>` still writes `title` as well, because a native tooltip survives a
 * disabled control where a focus-driven one cannot.
 */

import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

export interface TooltipProps {
  content: ReactNode
  side?: 'top' | 'bottom'
  children: ReactNode
  className?: string
}

export function Tooltip({ content, side = 'top', children, className }: TooltipProps) {
  const id = useId()
  const [open, setOpen] = useState(false)

  return (
    <span
      className={cx('relative inline-flex', className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span aria-describedby={id}>{children}</span>
      <span
        id={id}
        role="tooltip"
        hidden={!open}
        className={cx(
          'pointer-events-none absolute left-1/2 z-40 w-max max-w-[calc(220*var(--ud-u))] -translate-x-1/2 rounded-md border border-border-default bg-bg-raised px-1_5 py-1 text-caption text-text-primary shadow-popover',
          side === 'top' ? 'bottom-full mb-1' : 'top-full mt-1',
        )}
      >
        {content}
      </span>
    </span>
  )
}
